'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import logCellCoverageCalloutTapped from '@alltrails/amplitude/events/logCellCoverageCalloutTapped';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import type TrailDetails from '@alltrails/types/TrailDetails';
import IconContainer from '@alltrails/denali/components/IconContainer';
import TableRow from '@alltrails/denali/components/TableRow';
import CellSignalNone from '@alltrails/denali/icons/CellSignalNone';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import type { User } from '@alltrails/types/User';
import styles from './CellConnectivity.module.scss';

const DownloadUpsellModal = dynamic(() => import('@alltrails/upsell/components/DownloadUpsellModal/DownloadUpsellModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const CellConnectivityModal = dynamic(() => import('./CellConnectivityModal'), { ssr: false });

type CellConnectivityProps = {
  trailDetails: TrailDetails;
};

// https://www.figma.com/design/feR8cnkO4LP1JsfSH0q8aS/Cell-Connectivity--Handoff-?node-id=5679-69657&m=dev
export default function CellConnectivity({ trailDetails }: CellConnectivityProps) {
  const intl = useIntl();
  const user = useUser();
  const [isDownloadUpsellModalOpen, setIsDownloadUpsellModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isCellConnectivityModalOpen, setIsCellConnectivityModalOpen] = useState(false);

  return (
    <div className={styles.tableRow}>
      <button
        className={styles.connectivity}
        onClick={() => {
          logCellCoverageCalloutTapped({ trail_id: trailDetails.id });

          if (!user) {
            setIsSignupModalOpen(true);
            return;
          }

          if (!isSubscriber(user)) {
            setIsDownloadUpsellModalOpen(true);
            return;
          }

          setIsCellConnectivityModalOpen(true);
        }}
        type="button"
      >
        <TableRow
          className={styles.row}
          description={intl.formatMessage({ defaultMessage: 'People report poor cell coverage' })}
          label={intl.formatMessage({ defaultMessage: 'Spotty service expected' })}
          leftContent={
            <IconContainer className={styles.icon}>
              <CellSignalNone />
            </IconContainer>
          }
          rightContent={<ChevronSm orientation="right" />}
        />
      </button>
      {isDownloadUpsellModalOpen && (
        <DownloadUpsellModal
          isOpen={isDownloadUpsellModalOpen}
          onCloseRequest={(user?: User) => {
            setIsDownloadUpsellModalOpen(false);

            if (isSubscriber(user)) {
              setIsCellConnectivityModalOpen(true);
            }
          }}
          upsellTrigger="Cell Coverage Callout"
        />
      )}
      {isSignupModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignupModalOpen(false)}
          isOpen={isSignupModalOpen}
          onSuccess={user => {
            setIsSignupModalOpen(false);

            if (!isSubscriber(user)) {
              setIsDownloadUpsellModalOpen(true);
              return;
            }

            setIsCellConnectivityModalOpen(true);
          }}
          trigger="Cell Coverage Callout"
          upsellTrigger="Cell Coverage Callout"
        />
      )}
      {isCellConnectivityModalOpen && (
        <CellConnectivityModal
          isOpen={isCellConnectivityModalOpen}
          onCloseRequest={() => setIsCellConnectivityModalOpen(false)}
          trailDetails={trailDetails}
        />
      )}
    </div>
  );
}
